import type { ReactNode } from "react";

interface SplitStepLayoutProps {
  /** Large heading rendered in the left column. */
  title: ReactNode;
  /** Muted subtitle rendered below the title. */
  description?: ReactNode;
  /**
   * Primary call-to-action rendered at the bottom of the left column on
   * desktop and after the main content on mobile.
   */
  primaryAction?: ReactNode;
  /** Optional secondary action rendered above the primary action. */
  secondaryAction?: ReactNode;
  /**
   * Optional supplementary content rendered in the left column directly below
   * the description (e.g. a datasource switcher on the boards step).
   */
  aside?: ReactNode;
  /** Main content rendered in the right column. */
  children: ReactNode;
}

/**
 * Reusable two-column step layout used across onboarding and invitation flows.
 */
export const SplitStepLayout = ({
  title,
  description,
  primaryAction,
  secondaryAction,
  aside,
  children,
}: SplitStepLayoutProps) => {
  return (
    // Desktop uses a fixed height so every onboarding step shares the same
    // chrome dimensions and content doesn't make the card jump between steps.
    // Mobile stays fluid (height driven by content).
    <div className="relative grid w-full min-w-0 md:h-[560px] md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:grid-rows-[auto_1fr]">
      <div className="flex w-full min-w-0 flex-col gap-2 px-6 pt-8 md:col-start-1 md:row-start-1 md:bg-foreground/4 md:px-10 md:pt-10">
        <h1 className="font-semibold text-2xl tracking-tight">{title}</h1>
        {description ? (
          <div className="text-muted-foreground text-sm">{description}</div>
        ) : null}
        {aside ? <div className="mt-4">{aside}</div> : null}
      </div>

      <div className="flex w-full min-w-0 flex-col gap-4 px-6 py-8 md:col-start-2 md:row-span-2 md:row-start-1 md:min-h-0 md:overflow-y-auto md:px-10 md:py-10">
        {children}
      </div>

      <div className="flex w-full min-w-0 flex-col gap-2 px-6 pb-8 md:col-start-1 md:row-start-2 md:justify-end md:bg-foreground/4 md:px-10 md:pb-10">
        {secondaryAction}
        {primaryAction}
      </div>
    </div>
  );
};
